import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  masterDetailDetailRecipe,
  masterDetailListRecipe,
  masterDetailRecipe,
  type MasterDetailListRecipeProps,
} from '../recipes/master-detail.recipe';

export interface MasterDetailProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** The left pane: filters over a scrolling list. */
  list: React.ReactNode;
  /** The right pane: the selected record, or what to show when there is none. */
  detail: React.ReactNode;
  /**
   * How wide the list is. `full` with nothing selected; `narrow` once a
   * record is open; `collapsed` when the reader slides it away.
   */
  listState?: NonNullable<MasterDetailListRecipeProps['state']>;
  /** Whether the detail pane has slid in. */
  detailOpen?: boolean;
}

/**
 * A list beside the record picked from it.
 *
 *   <MasterDetail
 *     listState={selected ? (collapsed ? 'collapsed' : 'narrow') : 'full'}
 *     detailOpen={Boolean(selectedId)}
 *     list={<>filters, rows</>}
 *     detail={<>header, tabs</>}
 *   />
 *
 * Both panes stay in the DOM in every state and only their width and
 * opacity change, so the transition runs and nothing inside them remounts —
 * a half-typed filter survives the list sliding away. The two states are
 * separate props because the page drives them from different facts: the
 * list narrows once the record has *loaded*, the detail opens as soon as one
 * is *chosen*.
 */
export const MasterDetail = React.forwardRef<HTMLDivElement, MasterDetailProps>(
  function MasterDetail(
    {
      list,
      detail,
      listState = 'full',
      detailOpen = false,
      className,
      ...props
    },
    ref
  ) {
    return (
      <div ref={ref} className={cn(masterDetailRecipe(), className)} {...props}>
        <div className={masterDetailListRecipe({ state: listState })}>
          {list}
        </div>
        <div className={masterDetailDetailRecipe({ open: detailOpen })}>
          {detail}
        </div>
      </div>
    );
  }
);
